import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventDetailResponse } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { eventsApi } from '@/services/events.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';

/**
 * One event: its details and its registrations.
 *
 * The same split Groups draws between a group and its roster: names come from
 * People, resolved server-side at read time, so a registrant who marries shows
 * their current name here without Events storing or updating anything.
 */
export function EventDetailPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['events', id],
    queryFn: ({ signal }) => eventsApi.get(id, signal),
  });

  const registrationsQuery = useQuery({
    queryKey: ['events', id, 'registrations'],
    queryFn: ({ signal }) => eventsApi.listRegistrations(id, signal),
    enabled: query.isSuccess,
  });

  const onChanged = async (event: EventDetailResponse, message: string | null) => {
    queryClient.setQueryData(['events', id], event);
    await queryClient.invalidateQueries({ queryKey: ['events'], exact: false });
    setNotice(message);
  };

  const publish = useMutation({
    mutationFn: () => eventsApi.publish(id),
    onSuccess: (event) =>
      onChanged(event, t('events.event.published', { values: { name: event.name } })),
  });

  const cancelEvent = useMutation({
    mutationFn: (reason: string) => eventsApi.cancel(id, reason),
    onSuccess: (event) =>
      onChanged(event, t('events.event.cancelled', { values: { name: event.name } })),
  });

  const complete = useMutation({
    mutationFn: () => eventsApi.complete(id),
    onSuccess: (event) =>
      onChanged(event, t('events.event.completed', { values: { name: event.name } })),
  });

  const archive = useMutation({
    mutationFn: (reason: string) => eventsApi.archive(id, reason),
    onSuccess: (event) =>
      onChanged(event, t('events.event.archived', { values: { name: event.name } })),
  });

  const restore = useMutation({
    mutationFn: () => eventsApi.restore(id),
    onSuccess: (event) =>
      onChanged(event, t('events.event.restored', { values: { name: event.name } })),
  });

  const register = useMutation({
    mutationFn: (personId: string) => eventsApi.register(id, personId),
    onSuccess: async (event) => {
      await onChanged(event, null);
      await queryClient.invalidateQueries({ queryKey: ['events', id, 'registrations'] });
    },
  });

  const cancelRegistration = useMutation({
    mutationFn: (personId: string) => eventsApi.cancelRegistration(id, personId),
    onSuccess: async (event) => {
      await onChanged(event, null);
      await queryClient.invalidateQueries({ queryKey: ['events', id, 'registrations'] });
    },
  });

  if (query.isPending) return <Spinner label={t('common.states.loading')} />;

  if (query.isError) {
    return (
      <div className="page">
        <Link to="/events" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Alert tone="danger">{describeError(query.error, t)}</Alert>
      </div>
    );
  }

  const event = query.data;
  const isArchived = event.archivedAt !== null;
  const isFull = event.capacity !== null && event.registeredCount >= event.capacity;
  const registrations = registrationsQuery.data ?? [];
  const activeRegistrationIds = registrations
    .filter((registration) => registration.status !== 'CANCELLED')
    .map((registration) => registration.personId);

  const mutationError = [
    publish,
    cancelEvent,
    complete,
    archive,
    restore,
    register,
    cancelRegistration,
  ].find((mutation) => mutation.isError);

  return (
    <div className="page">
      <Link to="/events" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{event.name}</h1>
          <StatusBadge status={event.status} label={t(`events.status.${event.status}`)} />
        </div>

        <div className="page-actions">
          {can('events.event.publish') && event.status === 'DRAFT' && !isArchived && (
            <Button
              variant="secondary"
              loading={publish.isPending}
              onClick={() => publish.mutate()}
            >
              {t('events.event.publish')}
            </Button>
          )}
          {can('events.event.update') && event.status === 'PUBLISHED' && !isArchived && (
            <Button
              variant="secondary"
              loading={complete.isPending}
              onClick={() => complete.mutate()}
            >
              {t('events.event.complete')}
            </Button>
          )}
          {can('events.event.cancel') &&
            (event.status === 'DRAFT' || event.status === 'PUBLISHED') &&
            !isArchived && (
              <CancelButton event={event} onConfirm={(reason) => cancelEvent.mutate(reason)} />
            )}
          {can('events.event.archive') &&
            (isArchived ? (
              <Button
                variant="secondary"
                loading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                {t('common.actions.restore')}
              </Button>
            ) : (
              <ArchiveButton event={event} onConfirm={(reason) => archive.mutate(reason)} />
            ))}
        </div>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}
      {mutationError && <Alert tone="danger">{describeError(mutationError.error, t)}</Alert>}
      {isArchived && <Alert tone="warning">{t('events.event.archivedHint')}</Alert>}
      {!isArchived && event.status === 'DRAFT' && (
        <Alert tone="warning">{t('events.event.notPublishedHint')}</Alert>
      )}

      <dl className="detail-grid">
        <Detail label={t('events.event.description')} value={event.description} />
        <Detail label={t('events.event.category')} value={event.category} />
        <Detail label={t('events.event.location')} value={event.location} />
        <Detail
          label={t('events.event.startAt')}
          value={formatDateTime(event.startAt, locale)}
          dir="ltr"
        />
        <Detail
          label={t('events.event.endAt')}
          value={formatDateTime(event.endAt, locale)}
          dir="ltr"
        />
        <Detail
          label={t('events.event.capacity')}
          value={event.capacity === null ? null : String(event.capacity)}
          dir="ltr"
        />
      </dl>

      <section className="roster">
        <header className="page-header">
          <h2 className="page-title">
            {t('events.registration.title')} ·{' '}
            {t('events.event.registeredCount', { count: event.registeredCount })}
            {event.waitlistedCount > 0 &&
              ` · ${t('events.event.waitlistedCount', { count: event.waitlistedCount })}`}
          </h2>
          {can('events.registration.manage') && event.status === 'PUBLISHED' && !isArchived && (
            <RegisterPersonPicker
              existing={activeRegistrationIds}
              disabled={isFull}
              onPick={(personId) => register.mutate(personId)}
            />
          )}
        </header>

        {registrations.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('people.person.singular')}</th>
                  <th scope="col">{t('common.labels.status')}</th>
                  <th scope="col">{t('events.registration.registeredAt')}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {registrations.map((registration) => (
                  <tr key={registration.id}>
                    <td>
                      {/* Links back into People, the same as Groups' roster:
                          staff looking at a registrant usually want contact details next. */}
                      <Link to={`/people/${registration.personId}`} className="person-name">
                        {registration.displayName}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge
                        status={registration.status}
                        label={t(`events.registrationStatus.${registration.status}`)}
                      />
                    </td>
                    <td dir="ltr">{formatDateTime(registration.registeredAt, locale)}</td>
                    <td>
                      {can('events.registration.manage') &&
                        !isArchived &&
                        registration.status !== 'CANCELLED' && (
                          <Button
                            variant="ghost"
                            onClick={() => cancelRegistration.mutate(registration.personId)}
                            aria-label={`${t('events.registration.cancelled')}: ${registration.displayName}`}
                          >
                            {t('common.actions.cancel')}
                          </Button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// --- Pieces ------------------------------------------------------------------

function Detail({ label, value, dir }: { label: string; value: string | null; dir?: 'ltr' }) {
  return (
    <div className="detail-row">
      <dt className="detail-label">{label}</dt>
      <dd className="detail-value" dir={dir}>
        {value ?? <span className="cell-empty">—</span>}
      </dd>
    </div>
  );
}

/**
 * Picks someone from the directory to register.
 *
 * A search box rather than a full dropdown, the same choice Groups makes for
 * its roster: a church of two thousand cannot be a `<select>`.
 */
function RegisterPersonPicker({
  existing,
  disabled,
  onPick,
}: {
  existing: readonly string[];
  disabled: boolean;
  onPick: (personId: string) => void;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['people', 'picker', search],
    queryFn: ({ signal }) => peopleApi.list({ search, limit: 8 }, signal),
    enabled: search.trim().length >= 2,
  });

  const candidates = (query.data?.data ?? []).filter(
    (person) => !existing.includes(person.id) && !person.archivedAt,
  );

  return (
    <div className="member-picker">
      <Field
        label={t('events.registration.register')}
        name="registration-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('events.registration.choosePerson')}
        disabled={disabled}
      />

      {/* The full event says so rather than silently rejecting the click —
          registering still waitlists once server-side, this just tells staff
          up front what will happen. */}
      {!disabled && candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button
                variant="ghost"
                onClick={() => {
                  onPick(person.id);
                  setSearch('');
                }}
              >
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CancelButton({
  event,
  onConfirm,
}: {
  event: EventDetailResponse;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <Button variant="danger" onClick={() => setIsOpen(true)}>
        {t('events.event.cancel')}
      </Button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(formEvent: FormEvent) => {
        formEvent.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('events.event.cancelConfirm', { values: { name: event.name } })}</p>
      <Field
        label={t('events.event.cancelReason')}
        name="reason"
        value={reason}
        onChange={(formEvent) => setReason(formEvent.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="danger" disabled={reason.trim().length === 0}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function ArchiveButton({
  event,
  onConfirm,
}: {
  event: EventDetailResponse;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <Button variant="danger" onClick={() => setIsOpen(true)}>
        {t('common.actions.archive')}
      </Button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(formEvent: FormEvent) => {
        formEvent.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('events.event.archiveConfirm', { values: { name: event.name } })}</p>
      <Field
        label={t('events.event.archiveReason')}
        name="reason"
        value={reason}
        onChange={(formEvent) => setReason(formEvent.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="danger" disabled={reason.trim().length === 0}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
